import React, { useRef, useEffect } from "react";
import { motion } from "framer-motion";

const T = {
  bg: "#050d1a",
  accent: "#eab308",
  accentGlow: "rgba(234,179,8,0.4)",
  blue: "#3b82f6",
  blueGlow: "rgba(59,130,246,0.4)",
  textPrimary: "#f0f6ff",
  textMuted: "#4b6280",
};

/* ── Canvas: orbiting particle ring around the cube ── */
function OrbitField() {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    const S = 220;
    c.width = S;
    c.height = S;
    let t = 0, id;
    const cx = S / 2, cy = S / 2;

    const particles = Array.from({ length: 14 }, (_, i) => ({
      angle: (i / 14) * Math.PI * 2,
      radius: 78 + Math.random() * 14,
      speed: 0.012 + Math.random() * 0.008,
      tilt: Math.random() * 0.6 - 0.3,
      gold: i % 2 === 0,
      size: Math.random() * 1.8 + 0.8,
    }));

    function loop() {
      ctx.clearRect(0, 0, S, S);
      particles.forEach(p => {
        p.angle += p.speed;
        const x = cx + Math.cos(p.angle) * p.radius;
        const y = cy + Math.sin(p.angle) * p.radius * 0.4 + Math.sin(t + p.tilt) * 6;
        const depth = Math.sin(p.angle);
        const scale = 0.5 + (depth + 1) / 2 * 0.6;
        const alpha = 0.25 + (depth + 1) / 2 * 0.55;

        ctx.beginPath();
        ctx.arc(x, y, p.size * scale, 0, Math.PI * 2);
        ctx.fillStyle = p.gold
          ? `rgba(234,179,8,${alpha.toFixed(2)})`
          : `rgba(96,165,250,${(alpha * 0.8).toFixed(2)})`;
        ctx.fill();
      });

      /* faint orbit ellipse */
      ctx.beginPath();
      ctx.ellipse(cx, cy, 78, 78 * 0.4, 0, 0, Math.PI * 2);
      ctx.strokeStyle = "rgba(234,179,8,0.08)";
      ctx.lineWidth = 0.6;
      ctx.stroke();

      t += 0.02;
      id = requestAnimationFrame(loop);
    }
    loop();
    return () => cancelAnimationFrame(id);
  }, []);
  return (
    <canvas
      ref={ref}
      style={{
        position: "absolute", top: "50%", left: "50%",
        transform: "translate(-50%,-50%)",
        width: 220, height: 220,
        pointerEvents: "none", zIndex: 0,
      }}
    />
  );
}

export default function WorkersLoading({ showText = true, text = "SYNCING..." }) {
  const faces = [
    { rot: "rotateY(0deg) translateZ(32px)" },
    { rot: "rotateY(90deg) translateZ(32px)" },
    { rot: "rotateY(180deg) translateZ(32px)" },
    { rot: "rotateY(-90deg) translateZ(32px)" },
    { rot: "rotateX(90deg) translateZ(32px)" },
    { rot: "rotateX(-90deg) translateZ(32px)" },
  ];

  return (
    <div style={{
      display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      minHeight: 300, gap: 36, padding: 40,
      position: "relative",
      perspective: 1000,
    }}>

      {/* ── ambient dual-tone glow ── */}
      <motion.div
        animate={{ scale: [1, 1.35, 1], opacity: [0.18, 0.38, 0.18] }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
        style={{
          position: "absolute", top: "50%", left: "50%",
          width: 260, height: 260, transform: "translate(-50%,-50%)",
          background: `radial-gradient(circle,${T.accentGlow} 0%,transparent 70%)`,
          filter: "blur(40px)",
          borderRadius: "50%",
          zIndex: 0,
          pointerEvents: "none",
        }}
      />
      <motion.div
        animate={{ scale: [1.2, 1, 1.2], opacity: [0.15, 0.3, 0.15] }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 0.6 }}
        style={{
          position: "absolute", top: "50%", left: "50%",
          width: 220, height: 220, transform: "translate(-50%,-50%)",
          background: `radial-gradient(circle,${T.blueGlow} 0%,transparent 70%)`,
          filter: "blur(36px)",
          borderRadius: "50%",
          zIndex: 0,
          pointerEvents: "none",
        }}
      />

      {/* orbiting particle field */}
      <OrbitField />

      {/* ── 3D rotating cube ── */}
      <div style={{ position: "relative", zIndex: 1, width: 64, height: 64 }}>
        {/* base shadow ellipse */}
        <motion.div
          animate={{ scale: [1, 1.15, 1], opacity: [0.3, 0.15, 0.3] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          style={{
            position: "absolute", bottom: -40, left: "50%",
            transform: "translateX(-50%)",
            width: 60, height: 14, borderRadius: "50%",
            background: "rgba(234,179,8,0.25)",
            filter: "blur(6px)",
          }}
        />

        <motion.div
          style={{
            width: "100%", height: "100%",
            position: "relative",
            transformStyle: "preserve-3d",
          }}
          animate={{ rotateX: [0, 360], rotateY: [0, 360], rotateZ: [0, 360] }}
          transition={{ duration: 7, repeat: Infinity, ease: "linear" }}
        >
          {faces.map((f, i) => (
            <div
              key={i}
              style={{
                position: "absolute", inset: 0,
                background: "linear-gradient(135deg,#0f2040,#0a1628 60%,#1e3a5f)",
                border: `1.5px solid rgba(234,179,8,0.4)`,
                borderRadius: 14,
                boxShadow: `0 0 24px rgba(234,179,8,0.25), inset 0 1px 0 rgba(255,255,255,0.08)`,
                display: "flex", alignItems: "center", justifyContent: "center",
                overflow: "hidden",
                transform: f.rot,
                backfaceVisibility: "hidden",
              }}
            >
              {/* gold "3W" mark */}
              <span style={{
                color: T.accent,
                fontWeight: 900, fontSize: 22,
                fontFamily: "monospace",
                letterSpacing: "-0.05em",
                textShadow: "0 0 10px rgba(234,179,8,0.6)",
                userSelect: "none",
              }}>
                3W
              </span>

              {/* shine sweep */}
              <motion.div
                animate={{ x: ["-120%", "120%"] }}
                transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                style={{
                  position: "absolute", inset: 0,
                  background: "linear-gradient(105deg,transparent 30%,rgba(234,179,8,0.25) 50%,transparent 70%)",
                  transform: "rotate(-20deg)",
                }}
              />
            </div>
          ))}
        </motion.div>
      </div>

      {/* ── text ── */}
      {showText && (
        <div style={{ textAlign: "center", position: "relative", zIndex: 1 }}>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: [0.4, 1, 0.4] }}
            transition={{ duration: 2, repeat: Infinity }}
            style={{
              fontSize: 11, fontWeight: 900,
              textTransform: "uppercase", letterSpacing: "0.55em",
              color: T.textPrimary,
            }}
          >
            {text}
          </motion.div>

          {/* progress dots */}
          <div style={{ display: "flex", justifyContent: "center", gap: 6, marginTop: 14 }}>
            {[0, 1, 2].map(i => (
              <motion.span
                key={i}
                animate={{ opacity: [0.2, 1, 0.2], scale: [0.8, 1.1, 0.8] }}
                transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.2 }}
                style={{
                  width: 5, height: 5, borderRadius: "50%",
                  background: T.accent,
                  boxShadow: `0 0 8px ${T.accentGlow}`,
                }}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}